/**
 * WALL — config-driven zone grid, v2 (#269; design-council PART X, BINDING —
 * supersedes the PART V GOC/PEOC compositions; geometry refs
 * `docs/design-refs/ops-room-photos/wall-rooms-reference.md`).
 *
 * A wall is a CSS grid of ZONES; each zone hosts an existing COP surface. v2
 * rebuilds both configs for the MEASURED rooms (GOC ≈3:1 per segment, PEOC
 * ≈7:1 strip) after wall-QA round 1 proved the v1 compositions overflow/
 * collapse at the rooms' true aspects:
 *
 *   • `goc` (Ruling 1): centre COP hero ≈2:1 · one stacked full-bleed 16:9
 *     media flank per side (default 3 tiles, `flankRows` config-driven, 2
 *     supported) · room-wide bottom ticker. NO queue zone, NO mini-map — the
 *     queue lives at desks (#264 seat-claim; PART IX one-control-one-home),
 *     and the ≈2:1 Canada-frame hero IS the national overview.
 *   • `peoc` (Ruling 2): N equal seam-aligned panel columns
 *     (`?wall=peoc&panels=N`); end panels released (the no-queue law is
 *     WALL-GLOBAL); hero = parity-matched central span nearest 2:1 (≥3
 *     columns); flanks stacked 2-high, each tile 1 panel × half the content
 *     strip; room-wide ticker.
 *
 * THE ASPECT-FIT LAW (Ruling 3, structural): every config DECLARES its target
 * aspect(s) and MUST compose zero-overflow there. Geometry is DERIVED at
 * runtime from the measured viewport (`resolveTracks`) — pure pixel math, no
 * magic fr constants that only compose at one aspect, and testable at the
 * declared viewports (`wallAspectFit.test.ts`, the defaultViewLock pattern).
 * The ticker's 52px legibility floor is the ONLY permitted pixel minimum.
 *
 * Pure data + resolvers — no React — so the grid math is unit-testable.
 */

/** The kinds of zone a wall can host. Each maps 1:1 to a wall zone component.
 *  PART X removed 'group-queue' and 'mini-map' STRUCTURALLY: the no-queue law
 *  is wall-global (queue lives at desks, #264) and a ≈2:1 Canada-frame hero is
 *  its own national overview — a config can no longer host either, so the law
 *  cannot drift back by config edit alone. */
export type WallZoneKind = 'media' | 'cop-map' | 'ticker';

export interface WallZone {
  /** Stable id (React key + test handle). */
  id: string;
  kind: WallZoneKind;
  /** CSS `grid-column` line span, e.g. `'1 / 4'` (start / end grid lines). */
  gridColumn: string;
  /** CSS `grid-row` line span, e.g. `'2 / 3'`. */
  gridRow: string;
  /** Short human label (overlay caption / debug). */
  label: string;
  /** Media zone only — tile grid shape: `mediaCols × mediaRows` tiles (GOC
   *  flank = 1 × flankRows stack; PEOC flank = K panels × 2 rows). The bank's
   *  slot count is the product. */
  mediaCols?: number;
  mediaRows?: number;
  /**
   * Media zone only — index of the first feed this bank surfaces from the
   * shared alert-driven selection. Lets several media banks (the left + right
   * flanks) show DISTINCT feeds rather than repeating the same cameras: each
   * bank requests `feedOffset + slots` feeds and renders its `[feedOffset…]`
   * slice. Defaults to 0.
   */
  feedOffset?: number;
}

/** A declared target aspect (Ruling 3) — the canonical viewport(s) a config
 *  MUST compose at zero-overflow, pinned by `wallAspectFit.test.ts`. */
export interface WallTargetAspect {
  /** Human label, e.g. "GOC segment ≈3:1". */
  label: string;
  /** Canonical test viewport (the founder replication recipe). */
  width: number;
  height: number;
  /** PEOC only — the panel count this viewport is declared at. */
  panels?: number;
}

/** Resolved pixel tracks for a measured viewport: the wall grid's column
 *  widths and row heights. Rows are [content strip, ticker band]. */
export interface WallTracks {
  columns: number[];
  rows: number[];
}

export interface WallConfig {
  /** Registry key (the `?wall=<name>` value). */
  name: string;
  /** Human label shown in the wall's identity tag. */
  label: string;
  /** Declared target aspect(s) — the aspect-fit law's canonical viewports. */
  targetAspects: WallTargetAspect[];
  /** Where the identity banner lives: an overlay inside the hero (GOC has the
   *  room) or a compact corner tag at the wall's leftmost edge (PEOC — a
   *  centred header would eat a 7:1 strip; Ruling 2). Never a layout row. */
  identityPlacement: 'hero' | 'corner-tag';
  zones: WallZone[];
  /** Resolve the grid's pixel tracks for a measured viewport. Pure: all
   *  geometry DERIVES from (width, height) — rows always sum to height and
   *  columns to width, so the composition can never overflow its container. */
  resolveTracks: (width: number, height: number) => WallTracks;
  /** Media-reel dwell (ms) — how long each page of candidate feeds stays on
   *  screen before the reel rotates (#222). Omitted → `WALL_REEL_DWELL_MS`.
   *  Overridable per-room via `?dwell=N` (`resolveWallConfig`). */
  reelDwellMs?: number;
  /** Whether this wall auto-rotates curated YouTube-live NEWS into otherwise-empty
   *  media tiles (#334/#362). ONLY the **PEOC** strip does — its media flanks are
   *  the room's only news surface. The **GOC** room has dedicated standalone news
   *  TVs, so its wall must NEVER rotate news into a flank tile (it would duplicate
   *  the room's own news walls). When false, `WallMediaZone` passes an empty
   *  news-fallback set, so a quiet/short pool falls through to the picture/dead
   *  camera tiers (or an honest blank) instead of seating a news tile. */
  allowsNewsFallback: boolean;
}

/** The ticker band's legibility floor (px) — the ONLY pixel minimum permitted
 *  on a wall (Ruling 3.3). ≈19% of a 275px replication strip is the honest
 *  cost of a readable chyron; on real glass the fraction governs, not the
 *  floor. */
export const TICKER_MIN_PX = 52;
/** The ticker band's share of wall height when the floor isn't binding. */
export const TICKER_FRACTION = 0.05;

/** Ticker band height for a wall height — the floor or the fraction,
 *  whichever is taller. */
export function tickerBandPx(height: number): number {
  return Math.max(TICKER_MIN_PX, TICKER_FRACTION * height);
}

// ─────────────────────────────────────────────────────────────────────────────
// GOC v2 (Ruling 1)
//
//   ┌──────────┬─────────────────────────────┬──────────┐
//   │ MEDIA ×3 │                             │ MEDIA ×3 │
//   │ stacked  │      COP MAP (HERO ≈2:1)     │ stacked  │   content strip
//   │ 16:9     │                             │ 16:9     │
//   ├──────────┴─────────────────────────────┴──────────┤
//   │  TICKER · bulletins + wire / URGENT takeover       │   bottom band
//   └────────────────────────────────────────────────────┘
//
// Flank width is DERIVED, never hand-tuned: a column of `flankRows` full-bleed
// 16:9 tiles at the content-strip height is exactly
// `16 · H_content / (9 · flankRows)` wide — the identity that makes a 3-stack
// fill its column (3 × 9/16 ≈ 1.0 column heights) and lands the hero at
// ≈2.08:1 (Canada's shape) on the measured 3:1 room.
// ─────────────────────────────────────────────────────────────────────────────

/** Stacked tiles per GOC flank (Ruling 1: default 3; 2-stack supported). */
export const GOC_FLANK_ROWS_DEFAULT = 3;
export const GOC_FLANK_ROWS_SUPPORTED = [2, 3] as const;

/** Hero floor for UNDECLARED extreme viewports only: the hero never drops
 *  below this share of wall width (flanks shed the excess), so the wall
 *  degrades sanely on a shape no room declares (e.g. portrait). INERT at the
 *  declared aspects — `wallAspectFit.test.ts` pins the exact derived formula
 *  there, so this guard can never silently bind on a real room. */
export const GOC_HERO_MIN_SHARE = 0.4;

const clampGocFlankRows = (n: number | null | undefined): number => {
  const rows = n != null && Number.isFinite(n) ? Math.round(n) : GOC_FLANK_ROWS_DEFAULT;
  return (GOC_FLANK_ROWS_SUPPORTED as readonly number[]).includes(rows)
    ? rows
    : GOC_FLANK_ROWS_DEFAULT;
};

/** GOC flank width (px) for a viewport — the Ruling 1 derived formula plus the
 *  undeclared-extreme hero floor. */
export function gocFlankWidth(width: number, height: number, flankRows: number): number {
  const contentH = height - tickerBandPx(height);
  const derived = (16 * contentH) / (9 * flankRows);
  const heroFloorCap = (width * (1 - GOC_HERO_MIN_SHARE)) / 2;
  return Math.min(derived, heroFloorCap);
}

/** Build the GOC v2 config (Ruling 1). `flankRows` is config-driven (3
 *  default, 2 supported) — `?wall=goc&flankRows=2`. */
export function makeGocConfig(flankRows: number = GOC_FLANK_ROWS_DEFAULT): WallConfig {
  const rows = clampGocFlankRows(flankRows);
  return {
    name: 'goc',
    label: 'GOC · Government Operations Centre',
    targetAspects: [{ label: 'GOC segment ≈3:1', width: 1920, height: 640 }],
    identityPlacement: 'hero',
    // The GOC room has its own dedicated news TVs — its wall flanks must never
    // rotate news (#362). Cameras/pictures only.
    allowsNewsFallback: false,
    zones: [
      {
        id: 'media-left',
        kind: 'media',
        gridColumn: '1 / 2',
        gridRow: '1 / 2',
        label: 'Live Feeds',
        mediaCols: 1,
        mediaRows: rows,
        feedOffset: 0,
      },
      { id: 'cop', kind: 'cop-map', gridColumn: '2 / 3', gridRow: '1 / 2', label: 'Common Operating Picture' },
      {
        id: 'media-right',
        kind: 'media',
        gridColumn: '3 / 4',
        gridRow: '1 / 2',
        label: 'Live Feeds',
        mediaCols: 1,
        mediaRows: rows,
        // Continue the shared selection where the left bank ends → distinct feeds.
        feedOffset: rows,
      },
      { id: 'ticker', kind: 'ticker', gridColumn: '1 / 4', gridRow: '2 / 3', label: 'Wire' },
    ],
    resolveTracks: (width: number, height: number): WallTracks => {
      const ticker = tickerBandPx(height);
      const flank = gocFlankWidth(width, height, rows);
      return {
        columns: [flank, width - 2 * flank, flank],
        rows: [height - ticker, ticker],
      };
    },
  };
}

/** The default GOC config (3-stack flanks). */
export const GOC_WALL_CONFIG: WallConfig = makeGocConfig();

// ─────────────────────────────────────────────────────────────────────────────
// PEOC v2 (Ruling 2) — the true-7:1 strip wall
//
//   ┌────┬────┬────┬──────────────────┬────┬────┬────┐
//   │ M  │ M  │ M  │                  │ M  │ M  │ M  │   flank tiles: 1 panel
//   ├────┼────┼────┤  COP MAP (HERO)   ├────┼────┼────┤   × half strip, 2-high
//   │ M  │ M  │ M  │                  │ M  │ M  │ M  │   (the real room's DLP
//   ├────┴────┴────┴──────────────────┴────┴────┴────┤    cube grammar)
//   │  TICKER · room-wide                             │
//   └─────────────────────────────────────────────────┘
//
// End panels RELEASED (no queue / no mini — the no-queue law is wall-global);
// every zone boundary lands on a bezel seam (equal panel columns). Hero
// dominance AMENDED for strip walls: the hero is the single widest UNBROKEN
// full-height surface, dead centre — the flanks are diced into panel-sized
// tiles, so the map reads dominant even at ~29% of wall width.
// ─────────────────────────────────────────────────────────────────────────────

/** PEOC monitor-count bounds + default. Min 6 = 1 flank panel/side + a ≥4-wide
 *  parity-matched hero; above ~15 the panels get too thin to read. */
export const PEOC_MIN_PANELS = 6;
export const PEOC_MAX_PANELS = 15;
export const PEOC_DEFAULT_PANELS = 9;

/** Flank stack height (Ruling 2: 2-high, per the real room). */
export const PEOC_FLANK_ROWS = 2;

/** The declared PEOC viewport (Ruling 3: 7:1 @ 1920×275) — also the geometry
 *  the hero span is PLANNED at, so the zone grid is stable for a room. */
export const PEOC_DECLARED_VIEWPORT = { width: 1920, height: 275 } as const;

const clampPanels = (n: number): number =>
  Math.min(PEOC_MAX_PANELS, Math.max(PEOC_MIN_PANELS, Math.round(n)));

/** Hero span for an `n`-panel PEOC strip: the parity-matched central column
 *  count (S ≡ N mod 2, so the flanks split evenly and the hero sits DEAD
 *  CENTRE on whole panels) whose aspect at content-strip height lands nearest
 *  2:1 — never <3 columns, never so wide a flank loses its last panel.
 *  Planned at the DECLARED 7:1 room geometry (constants, not the live
 *  viewport) so a room's zone grid never re-plans under a stray resize. */
export function peocHeroSpan(panels: number): number {
  const n = clampPanels(panels);
  const { width, height } = PEOC_DECLARED_VIEWPORT;
  const contentH = height - tickerBandPx(height);
  const panelW = width / n;
  let best = -1;
  let bestDelta = Infinity;
  for (let s = n % 2 === 0 ? 4 : 3; s <= n - 2; s += 2) {
    const delta = Math.abs((s * panelW) / contentH - 2);
    if (delta < bestDelta) {
      best = s;
      bestDelta = delta;
    }
  }
  return best;
}

/** Column allocation for an `n`-panel PEOC strip (Ruling 2): all panels go to
 *  the hero + two symmetric stacked flanks — the end panels are RELEASED. */
export function peocColumnPlan(n: number): {
  panels: number;
  heroSpan: number;
  leftMedia: number;
  rightMedia: number;
} {
  const panels = clampPanels(n);
  const heroSpan = peocHeroSpan(panels);
  const perFlank = (panels - heroSpan) / 2;
  return { panels, heroSpan, leftMedia: perFlank, rightMedia: perFlank };
}

/** Build the PEOC wall config for a given monitor count
 *  (`?wall=peoc&panels=N`). */
export function makePeocConfig(panels: number = PEOC_DEFAULT_PANELS): WallConfig {
  const { panels: n, heroSpan, leftMedia, rightMedia } = peocColumnPlan(panels);

  // Walk the column grid lines left→right, placing each zone on an integer span.
  let line = 1;
  const take = (span: number): string => {
    const col = `${line} / ${line + span}`;
    line += span;
    return col;
  };

  const leftCol = take(leftMedia);
  const heroCol = take(heroSpan);
  const rightCol = take(rightMedia);
  const flankSlots = leftMedia * PEOC_FLANK_ROWS;

  return {
    name: 'peoc',
    label: `PEOC · Provincial Emergency Operations Centre · ${n} panels`,
    targetAspects: [
      { label: 'PEOC strip ≈7:1', width: 1920, height: 275, panels: 14 },
    ],
    identityPlacement: 'corner-tag',
    // The PEOC strip's media flanks ARE the room's news surface, so an empty
    // flank tile auto-fills with curated YouTube-live news (#362).
    allowsNewsFallback: true,
    zones: [
      {
        id: 'media-left',
        kind: 'media',
        gridColumn: leftCol,
        gridRow: '1 / 2',
        label: 'Live Feeds',
        mediaCols: leftMedia,
        mediaRows: PEOC_FLANK_ROWS,
        feedOffset: 0,
      },
      { id: 'cop', kind: 'cop-map', gridColumn: heroCol, gridRow: '1 / 2', label: 'Common Operating Picture' },
      {
        id: 'media-right',
        kind: 'media',
        gridColumn: rightCol,
        gridRow: '1 / 2',
        label: 'Live Feeds',
        mediaCols: rightMedia,
        mediaRows: PEOC_FLANK_ROWS,
        // Continue the shared selection where the left bank ends → distinct feeds.
        feedOffset: flankSlots,
      },
      { id: 'ticker', kind: 'ticker', gridColumn: `1 / ${n + 1}`, gridRow: '2 / 3', label: 'Wire' },
    ],
    resolveTracks: (width: number, height: number): WallTracks => {
      const ticker = tickerBandPx(height);
      // Equal panel columns — every zone boundary on a bezel seam.
      return {
        columns: Array.from({ length: n }, () => width / n),
        rows: [height - ticker, ticker],
      };
    },
  };
}

/** The default PEOC config (used when `?panels` is absent). */
export const PEOC_WALL_CONFIG: WallConfig = makePeocConfig();

/** Wall config registry, keyed by the `?wall=<name>` value. GOC + PEOC are
 *  parameterized (flankRows / panels) so the registry holds their defaults and
 *  `resolveWallConfig` rebuilds when params are supplied. */
export const WALL_CONFIGS: Record<string, WallConfig> = {
  goc: GOC_WALL_CONFIG,
  peoc: PEOC_WALL_CONFIG,
};

export const DEFAULT_WALL_CONFIG = GOC_WALL_CONFIG;

export interface ResolveWallConfigOpts {
  /** PEOC only — the room's monitor count (`?wall=peoc&panels=N`). */
  panels?: number | null;
  /** GOC only — stacked tiles per flank (`?wall=goc&flankRows=2`). */
  flankRows?: number | null;
  /** Reel dwell override (ms) from `?dwell=N` — applied to whichever config wins. */
  dwellMs?: number | null;
}

/**
 * Resolve a `?wall=<name>` param to a config. The legacy `?wall=1` (and any
 * unknown / truthy value) resolves to the default GOC config, so existing wall
 * bookmarks keep working. `?wall=peoc` rebuilds the strip for the supplied
 * `panels` count (clamped); GOC rebuilds for a supplied `flankRows` (2 or 3).
 * A `?dwell=N` (ms) override tunes the media-reel cadence for the room.
 */
export function resolveWallConfig(
  name: string | null | undefined,
  opts?: ResolveWallConfigOpts,
): WallConfig {
  const key = name?.toLowerCase();
  let config: WallConfig;
  if (key === 'peoc') {
    const panels = opts?.panels;
    // The single-hero default keeps the prebuilt config when no params are
    // supplied; a supplied panel count rebuilds the strip.
    config =
      panels != null && Number.isFinite(panels) ? makePeocConfig(panels) : PEOC_WALL_CONFIG;
  } else {
    const flankRows = opts?.flankRows;
    config =
      flankRows != null && Number.isFinite(flankRows)
        ? makeGocConfig(flankRows)
        : DEFAULT_WALL_CONFIG;
  }
  const dwell = opts?.dwellMs;
  if (dwell != null && Number.isFinite(dwell) && dwell > 0) {
    return { ...config, reelDwellMs: dwell };
  }
  return config;
}

/** The CSS grid-placement style for a zone (start/end lines). */
export function wallZoneStyle(zone: WallZone): { gridColumn: string; gridRow: string } {
  return { gridColumn: zone.gridColumn, gridRow: zone.gridRow };
}

/** CSS grid templates for resolved pixel tracks. Fractional px are valid CSS;
 *  the sums equal the viewport exactly, so the grid can never overflow. */
export function wallTrackTemplates(tracks: WallTracks): {
  gridTemplateColumns: string;
  gridTemplateRows: string;
} {
  return {
    gridTemplateColumns: tracks.columns.map((c) => `${c}px`).join(' '),
    gridTemplateRows: tracks.rows.map((r) => `${r}px`).join(' '),
  };
}

/** A media zone's slot count (tile grid product). */
export function mediaZoneSlots(zone: WallZone): number {
  return (zone.mediaCols ?? 1) * (zone.mediaRows ?? 1);
}
